/**
 * Lead Repository - Handles all API requests related to leads domain
 */

import api from "../../../shared/api";

/**
 * Fetch all leads with optional filters
 * @param {Object} filters - Filter criteria
 * @param {string} [filters.status] - Lead status filter
 * @param {string} [filters.source] - Lead source filter
 * @param {string} [filters.date] - Date filter (YYYY-MM-DD)
 * @param {string} [filters.dateFrom] - Inclusive createdAt lower bound (ISO)
 * @param {string} [filters.dateTo] - Inclusive createdAt upper bound (ISO)
 * @param {number} [filters.limit] - Limit results
 * @param {number} [filters.offset] - Offset for pagination
 * @returns {Promise<Object>} Leads data
 */
export const fetchLeads = async (params) => {
  // params is an object with query params

  const response = await api.get("/leads", { params: { ...params } });
  return response;
};

/**
 * Fetch a single lead by ID
 * @param {string} leadId - Lead identifier
 * @returns {Promise<Object>} Lead data payload
 */
export const fetchLead = async (leadId) => {
  const response = await api.get(`/leads/${leadId}`);
  // Shared api client returns the JSON body: { success, data, ... }
  return {
    success: response?.success !== false,
    data: response?.data ?? response,
    error: response?.error,
  };
};

/**
 * Fetch lead statistics
 * @returns {Promise<Object>} Lead statistics
 */
export const fetchLeadStats = async () => {
  const response = await api.get("/leads/stats");
  return response.data;
};

/**
 * Update lead status
 * @param {string} leadId - Lead identifier
 * @param {Object} updateData - Update data
 * @param {string} updateData.action - Action to perform (accept, reject, convert)
 * @param {string} [updateData.reason] - Reason for action
 * @param {string} [updateData.notes] - Additional notes
 * @returns {Promise<Object>} Update result
 */
export const updateLeadStatus = async (leadId, updateData) => {
  const response = await api.patch(
    `/leads/update/${leadId}/status`,
    updateData
  );
  return response.data;
};

/**
 * Update lead information
 * @param {string} leadId - Lead identifier
 * @param {Object} updateData - Update data
 * @param {string} [updateData.status] - Updated status (new, accepted, rejected, converted)
 * @param {string} [updateData.notes] - Admin notes
 * @param {Object} [updateData.contactInfo] - Updated contact information
 * @param {string} [updateData.category] - Updated category
 * @returns {Promise<Object>} Update result
 */
export const updateLead = async (leadId, updateData) => {
  const response = await api.patch(`/leads/update/${leadId}`, updateData);
  return response.data;
};
